// The migration timeline of one context: the diagram at a migration, colored with what that
// migration changed, and a track to move through the history. The inspector lists the changes.
import type { DockviewPanelApi } from 'dockview-react';
import { GitBranchPlus, Highlighter, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, Divider, ToolButton, type CanvasSelection, type CanvasTarget } from '../diagram/Canvas';
import { openSketch } from '../state/dock';
import { branchSketch } from '../state/sketches';
import { useCardFonts } from '../diagram/DiagramView';
import type { GraphOptions } from '../diagram/graph';
import { measureText } from '../diagram/measure';
import { useDiagramSettings } from '../diagram/settings';
import { useLayout, type LayoutInput } from '../diagram/useLayout';
import { INSPECTOR_DOCKS, useMedia } from '../lib/media';
import { showSidebar } from '../state/sidebars';
import { refKey, select, setActiveRef, useWorkspace, type ModelRef } from '../state/workspace';
import { Scrubber } from './Scrubber';
import { idsAt, keyAt, shownStep, stepGraph, unionLayoutInput } from './stepGraph';
import { useTimeline, useTimelineProgress } from './timeline';

const PLAY_INTERVAL = 1300;

/** Keys pressed here belong to the focused control, not to the timeline. */
function isTyping(target: EventTarget | null): boolean {
  // Expandable explorer items use the arrow keys themselves; migration items do not.
  return target instanceof Element && !!target.closest('input, textarea, select, [contenteditable="true"], .cm-editor, [role="slider"], [cmdk-root], .react-flow__node, [role="treeitem"][aria-expanded]');
}

export function TimelineView({ setKey, migrationId, nonce, api }: { setKey: string; migrationId: string | null; nonce?: number; api: DockviewPanelApi }) {
  const timeline = useTimeline(setKey, true);
  const progress = useTimelineProgress(setKey);
  const settings = useDiagramSettings();
  const source = useWorkspace((s) => s.source);
  const fontsReady = useCardFonts();
  const [index, setIndex] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [active, setActive] = useState(api.isActive);
  const [reveal, setReveal] = useState<{ id: string; nonce: number; focus?: boolean } | null>(null);
  // In focus the focused card shows every property, whatever the property mode.
  const [focused, setFocused] = useState(false);
  const inspectorDocks = useMedia(INSPECTOR_DOCKS);

  useEffect(() => {
    const d = api.onDidActiveChange((e) => setActive(e.isActive));
    return () => d.dispose();
  }, [api]);

  // The step asked for (explorer, command palette, restored tab), or the latest one.
  useEffect(() => {
    if (!timeline || timeline.steps.length === 0) return;
    const wanted = migrationId === null ? -1 : timeline.steps.findIndex((s) => s.id === migrationId);
    setIndex((current) => (wanted >= 0 ? wanted : current === null ? timeline.steps.length - 1 : Math.min(current, timeline.steps.length - 1)));
  }, [timeline, migrationId, nonce]);

  const go = useCallback(
    (to: number) => {
      if (!timeline) return;
      const next = Math.max(0, Math.min(timeline.steps.length - 1, to));
      setIndex(next);
      api.updateParameters({ setKey, migrationId: timeline.steps[next]!.id });
    },
    [timeline, api, setKey],
  );

  const shown = timeline && index !== null ? shownStep(timeline, index) : -1;
  const step = timeline && index !== null ? timeline.steps[index] : undefined;
  const shownRef: ModelRef | null = timeline && shown >= 0 ? timeline.steps[shown]!.ref : null;
  const previous = timeline && shown >= 0 && shown === index ? timeline.steps[shown]!.previous : null;
  const previousRef = timeline && previous !== null ? timeline.steps[previous]!.ref : null;
  const idAfter = useMemo(() => (timeline ? idsAt(timeline, shown >= 0 ? shown : null) : (k: string) => k), [timeline, shown]);
  const idBefore = useMemo(() => (timeline ? idsAt(timeline, previous) : (k: string) => k), [timeline, previous]);

  // Follow the selected entity from step to step, and tell the explorer which model is shown.
  const lastShown = useRef<number | null>(null);
  useEffect(() => {
    if (!timeline || shown < 0 || !shownRef) return;
    const from = lastShown.current;
    lastShown.current = shown;
    const selection = useWorkspace.getState().selection;
    if (from !== null && from !== shown && selection && selection.ref.setKey === setKey && timeline.steps[from] && refKey(selection.ref) === refKey(timeline.steps[from]!.ref)) {
      const v = selection.value;
      const entity = v.kind === 'entity' ? v.key.split('.')[0]! : v.kind === 'property' ? v.entity : null;
      const id = entity ? idsAt(timeline, from)(entity) : null;
      const next = id ? keyAt(timeline, shown, id) : undefined;
      if (v.kind === 'entity' && next) select(shownRef, { kind: 'entity', key: [next, ...v.key.split('.').slice(1)].join('.') });
      else if (v.kind === 'property' && next) select(shownRef, { ...v, entity: next });
      else select(shownRef, null);
    }
    if (active && step) setActiveRef(shownRef, { setKey, stepId: step.id });
  }, [timeline, shown, shownRef, step, active, setKey]);

  // Arrow keys move through the history while this tab is active.
  useEffect(() => {
    if (!active || index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
      const to = { ArrowLeft: index - 1, ArrowRight: index + 1, Home: 0, End: Number.MAX_SAFE_INTEGER }[e.key];
      if (to === undefined) return;
      e.preventDefault();
      setPlaying(false);
      go(to);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, index, go]);

  useEffect(() => {
    if (!playing || !timeline || index === null) return;
    if (index >= timeline.steps.length - 1) {
      setPlaying(false);
      return;
    }
    const timer = setTimeout(() => go(index + 1), PLAY_INTERVAL);
    return () => clearTimeout(timer);
  }, [playing, index, timeline, go]);

  const options = useMemo<GraphOptions>(
    () => ({ properties: settings.properties, collapseJoins: settings.collapseJoins, measure: measureText }),
    [settings.properties, settings.collapseJoins],
  );
  // The union layout keeps the property mode's sizes; only the shown step changes in focus.
  const stepOptions = useMemo<GraphOptions>(() => (focused ? { ...options, properties: 'all' } : options), [focused, options]);
  const graph = useMemo(() => (timeline && index !== null && fontsReady ? stepGraph(timeline, index, stepOptions) : null), [timeline, index, fontsReady, stepOptions]);

  const [input, setInput] = useState<LayoutInput | null>(null);
  useEffect(() => {
    if (!timeline || !fontsReady) return;
    let cancelled = false;
    void unionLayoutInput(timeline, options, () => cancelled).then((result) => {
      if (!cancelled && result) setInput(result);
    });
    return () => {
      cancelled = true;
    };
  }, [timeline, options, fontsReady]);
  const layoutKey = source ? `${source.id}|timeline|${setKey}|${settings.properties}` : null;
  const { positions, save, relayout } = useLayout(layoutKey, input);

  const selection = useWorkspace((s) => s.selection);
  const selected = useMemo<CanvasSelection>(() => {
    const none = { node: null, row: null, edge: null };
    if (!selection || !graph || !shownRef) return none;
    const key = refKey(selection.ref);
    const live = key === refKey(shownRef);
    if (!live && !(previousRef && key === refKey(previousRef))) return none;
    const nodeOf = (entityKey: string) =>
      graph.nodes.find((n) => (live ? n.diff !== 'removed' && n.key === entityKey : n.diff === 'removed' ? n.key === entityKey : n.previousKey === entityKey || (!n.previousKey && n.key === entityKey)))?.id ?? null;
    const v = selection.value;
    if (v.kind === 'entity') return { ...none, node: nodeOf(v.key) };
    if (v.kind === 'property') {
      const node = nodeOf(v.entity);
      return { ...none, row: node ? `${node}::${v.property}` : null };
    }
    return { ...none, edge: live ? `rel:${v.index}` : `was:rel:${v.index}` };
  }, [selection, graph, shownRef, previousRef]);

  const onSelect = useCallback(
    (target: CanvasTarget | null) => {
      if (!shownRef) return;
      if (!target) return select(shownRef, null);
      if (target.kind === 'node') {
        const removed = target.node.diff === 'removed' && previousRef;
        return select(removed ? previousRef : shownRef, { kind: 'entity', key: target.node.key });
      }
      if (target.kind === 'row') {
        if (target.row.diff === 'removed' && previousRef) {
          const entity = target.node.diff === 'removed' ? target.node.key : (target.node.previousKey ?? target.node.key);
          return select(previousRef, { kind: 'property', entity, property: target.row.id });
        }
        return select(shownRef, { kind: 'property', entity: target.node.key, property: target.row.id });
      }
      const index = target.edge.relationship;
      if (index === null) return;
      select(target.edge.diff === 'removed' && previousRef ? previousRef : shownRef, { kind: 'relationship', index });
    },
    [shownRef, previousRef],
  );

  // Entities to center on, from the explorer, the command palette and the list of changes.
  const request = useWorkspace((s) => s.reveal);
  useEffect(() => {
    if (!request || !shownRef) return;
    const key = request.key.split('.')[0]!;
    if (refKey(request.ref) === refKey(shownRef)) setReveal({ id: idAfter(key), nonce: request.nonce, focus: request.focus });
    else if (previousRef && refKey(request.ref) === refKey(previousRef)) setReveal({ id: idBefore(key), nonce: request.nonce, focus: request.focus });
  }, [request, shownRef, previousRef, idAfter, idBefore]);

  const efVersion = useWorkspace((s) => (shownRef ? s.models[refKey(shownRef)]?.result.efVersion : undefined));

  if (!timeline) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-muted">
        <span className="flex items-center gap-2">
          <Loader2 size={16} className="animate-spin" /> Reading migrations{progress ? ` · ${progress.done} of ${progress.total}` : '…'}
        </span>
        {progress && progress.total > 0 && (
          <span className="h-1 w-56 overflow-hidden rounded-full bg-panel-2">
            <span className="block h-full bg-accent transition-[width]" style={{ width: `${(100 * progress.done) / progress.total}%` }} />
          </span>
        )}
      </div>
    );
  }
  if (timeline.steps.length === 0) {
    return <div className="flex h-full items-center justify-center p-6 text-muted">This context has no migrations yet.</div>;
  }
  if (index === null || !step) return null;

  const model = shown >= 0 ? timeline.steps[shown]!.model! : null;
  return (
    <div className="flex h-full flex-col" data-testid="timeline">
      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          {graph && positions ? (
            <Canvas
              graph={graph}
              positions={positions}
              selected={selected}
              onSelect={onSelect}
              onMoved={save}
              onRelayout={() => void relayout()}
              fitKey={`timeline:${setKey}`}
              reveal={reveal}
              onFocusChange={setFocused}
              dimUnchanged={settings.dimUnchanged && shown === index}
              status={
                model && (
                  <>
                    {Object.keys(model.entities).length} entities · {model.relationships.length} relationships
                    {efVersion && <> · EF Core {efVersion}</>}
                  </>
                )
              }
              tools={
                <>
                  <ToolButton label={settings.dimUnchanged ? 'Show all cards at full strength' : 'Fade cards that did not change'} active={settings.dimUnchanged} onClick={settings.toggleDimUnchanged}>
                    <Highlighter size={15} />
                  </ToolButton>
                  <Divider />
                  <ToolButton label="Start a sketch from the model at this migration" onClick={() => shownRef && void branchSketch(shownRef).then((s) => s && openSketch(s.id))}>
                    <GitBranchPlus size={15} />
                  </ToolButton>
                </>
              }
            />
          ) : (
            <div className="flex h-full items-center justify-center gap-2 text-muted">
              {shown < 0 ? (
                'The model at this migration is unknown: it has no .Designer.cs file.'
              ) : (
                <>
                  <Loader2 size={16} className="animate-spin" /> Arranging…
                </>
              )}
            </div>
          )}
          {!step.model && shown >= 0 && (
            <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
              <p className="rounded-md border border-border bg-panel-2/95 px-3 py-1.5 text-[12px] text-muted shadow-sm">
                {step.name} has no .Designer.cs file, so this is the model after {timeline.steps[shown]!.name}.
              </p>
            </div>
          )}
        </div>
      </div>
      <Scrubber
        steps={timeline.steps}
        index={index}
        onChange={(i) => {
          setPlaying(false);
          go(i);
        }}
        playing={playing}
        onTogglePlay={() => {
          if (!playing && index >= timeline.steps.length - 1) go(0);
          setPlaying(!playing);
        }}
        onShowChanges={inspectorDocks ? undefined : () => showSidebar('inspector')}
      />
    </div>
  );
}
